import { useAuth0 } from '@auth0/auth0-react';
import { createContext, useContext, useMemo, useRef, type ReactNode } from 'react';

import { useTenant } from './tenant-context';

import { DEFAULT_REGION, getApiUrlForRegion } from '@/lib/region-config';
import {
    ApiClient,
    UsersService,
    TenantsService,
    InvitationsService,
    SubscriptionsService,
    PlansService,
    RegionsService,
    UsageService,
    WorkflowsService,
    CasesService,
    DataService,
    JobsService,
    WorkflowStateService,
    GatewaysService,
    InputDatasetsService,
    SnapshotsService,
    LegalService,
    BillingService,
    PurchaseIntentsService,
} from '@/services';

interface ApiServices {
    users: UsersService;
    tenants: TenantsService;
    invitations: InvitationsService;
    subscriptions: SubscriptionsService;
    plans: PlansService;
    regions: RegionsService;
    usage: UsageService;
    workflows: WorkflowsService;
    cases: CasesService;
    data: DataService;
    jobs: JobsService;
    workflowState: WorkflowStateService;
    gateways: GatewaysService;
    inputDatasets: InputDatasetsService;
    snapshots: SnapshotsService;
    legal: LegalService;
    billing: BillingService;
    purchaseIntents: PurchaseIntentsService;
}

const ApiContext = createContext<ApiServices | undefined>(undefined);

export function ApiProvider({ children }: { children: ReactNode }) {
    const { getAccessTokenSilently } = useAuth0();
    const { tenantId, tenantRegion } = useTenant();

    // Use refs so callbacks always get the latest values without recreating services
    const tenantIdRef = useRef<string | null>(tenantId);
    tenantIdRef.current = tenantId;

    // The region of the tenant in context, so requests follow a switch on the
    // render it happens (ESCLOUD-269). Unknown region falls back to the default
    // one, which is also what bootstraps the first call before a tenant exists.
    const tenantRegionRef = useRef<string | null>(tenantRegion);
    tenantRegionRef.current = tenantRegion;

    const services = useMemo(() => {
        const client = new ApiClient({
            getBaseUrl: () => getApiUrlForRegion(tenantRegionRef.current ?? DEFAULT_REGION),
            getAccessToken: getAccessTokenSilently,
            getTenantId: () => tenantIdRef.current,
        });

        return {
            users: new UsersService(client),
            tenants: new TenantsService(client),
            invitations: new InvitationsService(client),
            subscriptions: new SubscriptionsService(client),
            plans: new PlansService(client),
            regions: new RegionsService(client),
            usage: new UsageService(client),
            workflows: new WorkflowsService(client),
            cases: new CasesService(client),
            data: new DataService(client),
            jobs: new JobsService(client),
            workflowState: new WorkflowStateService(client),
            gateways: new GatewaysService(client),
            inputDatasets: new InputDatasetsService(client),
            snapshots: new SnapshotsService(client),
            legal: new LegalService(client),
            billing: new BillingService(client),
            purchaseIntents: new PurchaseIntentsService(client),
        };
    }, [getAccessTokenSilently]);

    return <ApiContext.Provider value={services}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiServices {
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error('useApi must be used within an ApiProvider');
    }
    return context;
}
